import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface DeprecatedRouteProps {
  newPath: string;
  label: string;
}

/**
 * Shown when a retired/legacy route is accessed directly.
 * Logs the access to admin_audit_log, auto-redirects after 5s,
 * and offers an instant "Go to new page" button.
 *
 * RETIRED ROUTE MAP:
 *   /admin/compliance/monitoring-parameters → /admin/compliance/tm/parameters
 *   /admin/cards/fees                       → /admin/cards/fee-rules
 */
export default function DeprecatedRoute({ newPath, label }: DeprecatedRouteProps) {
  const navigate = useNavigate();

  useEffect(() => {
    // Log deprecated access
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        supabase.from('admin_audit_log').insert({
          actor_id: data.user.id,
          actor_email: data.user.email ?? null,
          action: 'deprecated_route_accessed',
          target_type: 'route',
          target_id: window.location.pathname,
          after_state: { redirected_to: newPath },
        }).then(() => {});
      }
    });

    const timer = setTimeout(() => navigate(newPath, { replace: true }), 5000);
    return () => clearTimeout(timer);
  }, [navigate, newPath]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center p-8">
      <div className="rounded-full bg-yellow-500/10 p-4">
        <AlertTriangle className="w-10 h-10 text-yellow-500" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Page Moved</h1>
        <p className="text-muted-foreground max-w-sm">
          <strong>{label}</strong> has moved to a new location. You will be redirected automatically in 5 seconds.
        </p>
      </div>
      <Button onClick={() => navigate(newPath, { replace: true })}>
        Go to {label}
      </Button>
      <p className="text-xs text-muted-foreground">New path: <code className="font-mono">{newPath}</code></p>
    </div>
  );
}
